/**
 * WHAT THIS SHOP CALLS THE THING A JOB IS ABOUT.
 *
 * Anthony, 2026-09-15: a shop desk for swiftgaragedoorslv.com. Swift fixes
 * garage doors, and every screen in this system says "Vehicle", asks for a
 * VIN and offers an odometer reading.
 *
 * ─── WHY THIS IS NOT A RENAME ──────────────────────────────────────────
 *
 * Supercanic and Wrench Services run this same code and both ARE auto
 * shops. "Vehicle" is not a mistake to be corrected; it is correct for
 * them and wrong for Swift. So the words are looked up from the account's
 * trade (migration 0107) rather than written into the screens, and a shop
 * that never sets one keeps exactly the words it has today.
 *
 * ─── PURE, AND IN src/domain, BECAUSE THE SERVER SAYS THESE WORDS TOO ──
 *
 * A quote chase text reads "did you get the quote for the 2019 Honda
 * Pilot", and that sentence is built in server/tasks/quote-chase.ts. If the
 * vocabulary lived in the UI, a garage door customer would read the right
 * word on the screen and the wrong one in the text. So it lives here, with
 * no imports, the way src/domain/commission.ts and place.ts already do.
 *
 * ─── ADDING A TRADE ────────────────────────────────────────────────────
 *
 * Add it to `Trade`, add its row to WORDS, and widen the check constraint
 * in a migration. The test asserts every trade in the type has a complete
 * row, so a half-added trade fails the build rather than rendering the word
 * "undefined" at a customer.
 */

/** The trades this system has words for. Mirrors accounts_trade_known. */
export type Trade =
  | "auto" | "garage_door" | "locksmith" | "investigations" | "headlight"
  | "marketing" | "rv" | "service" | "remodeling" | "restoration";

export type Vocabulary = {
  /** The thing itself, lower case, for the middle of a sentence. */
  thing: string;
  /** More than one of them, lower case. */
  things: string;
  /**
   * What a sentence says when we do not know which one. Carries its own
   * article and is never tested for emptiness -- see carphrase.ts, where
   * a `|| "car"` fallback next to a non-empty string could never fire and
   * six customers got "the quote for the No vehicle".
   */
  theirs: string;
  /** A row we cannot name, as a table cell reads it. */
  unidentified: string;
  /** The identifier stamped on it by whoever built it. */
  serial: string;
  /**
   * The wear reading, or null for a trade that has none worth asking for.
   * Null hides the field rather than showing an empty one somebody has to
   * wonder about.
   */
  reading: string | null;
  /** What the browse screen can be searched by, as prose for its subtitle. */
  searchBy: string;
  /** Whether a license plate is a thing this trade's records have. */
  hasPlate: boolean;
  /** Whether asking what engine is in it makes any sense. */
  hasEngine: boolean;
  /**
   * What the `trim` column is called here. On a car it is the trim level;
   * on a door it is what the thing is made of, which is the field a
   * technician actually wants in that slot.
   */
  trimLabel: string;
  /**
   * The placeholder in the `trim` box, where the trade has its own.
   *
   * Optional because the job card's old ternary -- "LT, Limited, Sport" if
   * the trade has an engine, "Steel, wood, aluminium, glass" otherwise --
   * is what every trade before rv shows, and this is not the change to
   * move them. An RV has no engine field and its trim is its class, so the
   * door's materials would have been the example under "Class".
   */
  trimExample?: string;
  /**
   * The line under the identity fields, saying why they are worth getting
   * right. It names the object, so it cannot be one sentence for both.
   */
  whyItMatters: string;
  /**
   * A realistic one, for a placeholder. A placeholder is the fastest
   * instruction on a form -- "2014 Toyota Tundra" tells somebody the shape
   * of the answer without a word of help text -- and it is worthless if it
   * names the wrong kind of object.
   */
  example: string;
  /** Where a technician standing at the thing finds its serial. */
  serialHint: string;
  /** A real manufacturer and a real model, for the two adjacent boxes. */
  makeExample: string;
  modelExample: string;

  /**
   * ─── THE FIELDS THEMSELVES, NOT JUST THEIR LABELS ──────────────────
   *
   * Anthony: "make things like garage door brand, color, repair needed,
   * make it about garage doors." Relabelling `make` to "Brand" is half the
   * job; the other half is showing the fields this trade actually uses and
   * hiding the ones it does not.
   *
   * All three of those already have a home, which is why no schema change
   * was needed: brand is `make`, color is the `colour` column (it has
   * existed since 0001 and the AI agent already writes to it -- no screen
   * has ever shown it), and "repair needed" is the job's `complaint`.
   */

  /** What the manufacturer is called here. A door has a brand, not a make. */
  makeLabel: string;
  /**
   * What the second identity box is called. It was hardcoded as "Model",
   * which is right for anything manufactured and wrong for a trade whose
   * record is not an object at all -- an investigator's case has a matter
   * and a work type, and neither of them is a model. Added with the fourth
   * trade; the first three all say "Model" and nothing moved for them.
   */
  modelLabel: string;
  /** What the year means. On a door it is when it went in. */
  yearLabel: string;
  /**
   * Whether color is worth a field. It matters enormously on a door -- a
   * replacement panel has to match -- and has never been shown for a car,
   * so turning it on for auto now would be a change nobody asked for.
   */
  hasColour: boolean;
  colourLabel: string;
  /** What the job's `complaint` field is called, and its hint. */
  complaintLabel: string;
  complaintHint: string;

  /**
   * ─── SAYING WHICH ONE YOU WORKED ON ────────────────────────────────
   *
   * A job cannot close until the record says which object it was. That
   * rule is not bureaucracy -- src/data/jobs.ts calls it "the question
   * that arrives with a lawyer" -- and it stays.
   *
   * What it is SATISFIED BY is what varies. It was hardcoded as "a plate
   * or a VIN", which a garage door can never have, so Swift's every job
   * would have been unclosable without an override. A door says which one
   * it is by its serial, or by brand and model, or by the free-text
   * description ("16x7 double, LiftMaster 8500 side mount").
   */
  identifyTitle: string;
  identifySub: string;

  /**
   * ─── THE PAGE THE CUSTOMER OPENS ───────────────────────────────────
   *
   * garage.html is what a customer taps in a text, and its three visible
   * strings used to be a ternary in server/desk/shop-home.ts: garage door
   * or, failing that, Supercanic's words. That shape answers a third trade
   * with "Your Garage" and a "Vehicle inspection report", which is the
   * exact failure this module exists to stop -- so the words live here
   * with the rest and the server reads them.
   *
   * Auto's three are byte identical to what shipped before, because every
   * existing customer link points at that page.
   */
  customerTitle: string;
  customerOpening: string;
  customerReport: string;
  /** The sentence shown when a job is blocked on it. */
  identifyBlocked: string;

  /**
   * ─── WHO DOES THE WORK, AND WHAT IS BILLED BESIDE THE HOURS ────────
   *
   * Anthony, 2026-09-18: "do the vocabulary pass for technician and
   * parts."
   *
   * THE ROLE AND THE LINE KIND DO NOT MOVE. `technician` is a value in
   * the Role union and `part` is a line kind the database and the
   * commission split both read. Renaming either would be a migration
   * pretending to be a wording change. These are the LABELS only: what a
   * person is called on a screen, never what the row is called.
   *
   * WHY A FIRM THAT SELLS HOURS STILL HAS THE SECOND ONE. NYIA bills a
   * vehicle fee per day and passes through database searches and filing
   * fees. Those are real non-labour lines; they are simply not "parts",
   * and calling them that on an invoice a client reads is the tell that
   * the software was built for somebody else.
   */
  /** One of the shop's people, on screen. Never the Role value. */
  techLabel: string;
  techLabelPlural: string;
  /** A non-labour line, on screen. Never the `part` line kind. */
  partLabel: string;
  partsLabel: string;
  /**
   * What the `part_number` column is called, or NULL for a trade whose
   * non-labour lines do not carry one.
   *
   * Null the same way `reading` is null: it HIDES the field rather than
   * relabelling it. Rendering the vocabulary into the old sentence gave
   * "brings its cost and expense number with it" and a box headed
   * "expense no." -- and there is no such thing. NYIA's non-labour lines
   * are a vehicle fee per day, database searches and filing fees. None of
   * them has a catalog number, and a box asking for one is a box that
   * stays empty on every job for ever.
   */
  partNumberLabel: string | null;
  /**
   * Whether a VEHICLE parts catalog is a thing this trade could ever look
   * something up in.
   *
   * Anthony, 2026-09-18: "hide the parts lookup page for non-auto trades."
   *
   * It gates the catalog row on Everything Else and the Manuals shelf --
   * recalls, wiring diagrams, factory portals, a VIN decoder. Every source
   * behind both is a vehicle manufacturer, so neither is fixable by
   * renaming anything: a lock shop has real parts with real part numbers
   * and still has nothing to find in there.
   *
   * NOT the same question as partNumberLabel. A locksmith has part
   * numbers and no vehicle catalog; the two must not be collapsed.
   */
  hasPartsCatalog: boolean;

  /**
   * ─── WHAT THE DESK SAYS TO A STRANGER ──────────────────────────────
   *
   * Every message in server/tasks/lead-textback.ts was written for a
   * repair shop and hardcoded there, and all of them predate this module.
   * On 2026-09-17 NYIA's desk went live with the opener still asking
   * "what's the year, make and model, and what's it doing?" -- to people
   * ringing a private investigator about a spouse. It was frozen before it
   * sent anything.
   *
   * These are the strings a person who has never dealt with the shop
   * reads first, which makes them the ones a wrong trade shows up in
   * fastest. They live here so adding a trade forces somebody to answer
   * them, rather than inheriting a car.
   */

  /** After "sorry we missed your call." Carries its own promise, because
   *  what can be promised differs: a shop can quote a brake job off a
   *  text and nobody can quote a surveillance without scoping it. */
  missedCallAsk: string;
  /** The owner's short second message, in their own voice. */
  nudgeAsk: string;
  /** They texted in and did not say what it is about. */
  textedInAsk: string;
  /** They texted in and DID say. The next thing worth asking. */
  textedInNext: string;
  /** What the customer's own page is called out loud in a text. */
  /** Tacked onto a texted-in reply. Empty for a trade that cannot honour it:
   *  an investigator's hours are scoped by a person before anybody says a
   *  number, and the drafter refuses rather than guess. Its OWN fact, not a
   *  reading of hasNightRate -- an earlier version did that and got headlight
   *  wrong, which has no night rate and quotes off a text all day. */
  textedInPromise: string;
  portalLabel: string;
  /**
   * The question the AFTER-HOURS opener asks, or null for a trade with no
   * night rate at all.
   *
   * Null is load bearing twice over. That copy quotes a number off
   * `labour_rate ?? 150` and calls it "double our normal", so on a trade
   * that never set one it invents a price and texts it to a stranger at
   * eleven at night. And it asked "what's the vehicle" of everybody,
   * which is wrong for four of the five trades here. One field answers
   * both: no question, no night opener.
   */
  nightAsk: string | null;
  /**
   * THE ONE QUESTION PUT TO SOMEBODY WHO FILLED IN A FORM.
   *
   * The form already carried their name, their number and what they want.
   * What it never carries is when they are free, so that is the ask.
   *
   * ─── THIS WAS THE ONE THAT WAS JUDGED TRADE-PROOF, AND WAS NOT ─────────
   *
   * The 2026-09-17 vocabulary pass moved five asks onto this type and left
   * FROM_FORM alone, on the reasoning that "when are you free and what's
   * the address" survives a change of trade intact. On 2026-09-18 a real
   * client filled in NYIA's form from the matrimonial page and was asked
   * for an address as the first thing the firm ever said to him.
   *
   * A mobile trade asks for an address because somebody is driving to it.
   * An investigator has no address to go to, and asking a matrimonial
   * client for one reads as asking for the SUBJECT's.
   *
   * It survived the audit because `nyia-surface.test.ts` greps for
   * forbidden words -- investigat, surveill, spouse, affair -- and this
   * sentence contains none of them. A test that hunts words cannot catch a
   * sentence that is merely wrong for the trade, which is why this is a
   * field every trade must answer rather than a string to check.
   */
  formAsk: string;
  /**
   * WHAT THE TEXT CONVERSATION AFTER THE OPENER IS FOR.
   *
   * Anthony, 2026-09-28, about WeRV, an RV rental yard in Hemet: keep it
   * "focused on rentals". The intake agent (server/tasks/intake-agent.ts)
   * was written for repair. It asks for the plate, the symptoms, mobile or
   * shop, and whether the car is somewhere safe to work on, and it closes
   * by promising a price. Put to somebody planning a camping trip, every
   * one of those is the wrong question, and the closing line promises the
   * one thing a rental yard must not text a stranger.
   *
   * So the trade says which conversation it gets. Absent means repair,
   * which is what every trade before this had, so nothing moves for them.
   * `"rental"` asks for the trip instead: dates, how many are going, where
   * they are headed, delivered or picked up. Read through `intakeKind()`
   * below, never by comparing a trade name, so a second rental trade is a
   * row here and not a hunt through the agent for `=== "rv"`.
   */
  intakeKind?: "repair" | "rental";

  /**
   * ─── WHAT A QUOTE IS, IN THIS TRADE ───────────────────────────────
   *
   * Anthony, 2026-09-29, about Swift: "modify quotes a bit, we just want a
   * quote to be the customer saying yes to a free visit for an estimate.
   * This means quotes can auto send because they are not estimates, just
   * confirmation a person wants an appointment. Next step is schedule
   * still."
   *
   * A garage door is priced standing in front of it. The spring size, the
   * track, whether the opener is worth saving, none of that survives being
   * guessed from a text, so the first thing Swift offers is to come and
   * look, for free. The "quote" is the customer saying yes to that visit,
   * and it carries no price at all. src/domain/visitquote.ts has the rest.
   *
   * True for garage_door alone. Every other trade still sells a priced
   * quote, exactly as before, and reads false here.
   */
  quoteIsVisit: boolean;
  /**
   * What the quote is called on a screen and in a text: "quote" for a
   * trade that prices one, "free estimate visit" for a trade whose quote
   * is an offer to come and look. Lower case, for the middle of a
   * sentence; cap() it for a heading.
   */
  quoteLabel: string;

  /**
   * ─── A CASE FILE, NOT A WORKSHOP ───────────────────────────────────
   *
   * Neelum at NYIA, 2026-10-14, after an evening of hunting for the
   * agreement button: "It is really hard to find what is needed. Please
   * remove all car related verbiage and entirely remove all the extra
   * stuff and only keep the below list: phone number and name, phone
   * recording, phone transcript, SEND AGREEMENT button for Docusign."
   * Her screenshot circled "No technician / Advisor: Nobody yet".
   *
   * Relabelling did not get there, because what was in the way was not
   * words but furniture: who is on it and who is the advisor, the
   * technician and service advisor walk cards, the inspection sheet, the
   * diagnostic card, a VIN-shaped identity block, the AI drafter. Each is
   * right for a shop with bays and wrong for a firm that sells hours and
   * a signed retainer.
   *
   * True hides that furniture on the lead sheet and the job card, and
   * puts Send agreement first. Absent is every other trade, unchanged.
   */
  caseFile?: boolean;
  /**
   * A water damage, mold, fire and storm restoration company. Draws the
   * loss file on the job card (src/pages/restoration.ts): the carrier,
   * claim and adjuster, category and class, moisture readings, the
   * equipment on site and the drying days. Only 'restoration' sets it.
   */
  lossFile?: boolean;
};

/** The columns identification can be read from, whatever the trade. */
export type Identifiable = {
  license_plate?: string | null;
  vin?: string | null;
  make?: string | null;
  model?: string | null;
  description?: string | null;
} | null | undefined;

const has = (v: unknown): boolean => String(v ?? "").trim().length > 0;

/**
 * IS THERE ENOUGH ON THE RECORD TO SAY WHICH ONE THIS WAS.
 *
 * Pure, so the browser and the server answer identically -- they used to
 * compute this in four separate places, each with the same hardcoded
 * `plate || vin`.
 *
 * Auto is unchanged, deliberately: a plate or a VIN, and neither a make nor
 * a description will do. A car with "silver Honda" on it is not identified
 * for the purpose this rule exists for.
 */
export function isIdentified(trade: string | null | undefined, v: Identifiable): boolean {
  if (!v) return false;
  if (words(trade).hasPlate) return has(v.license_plate) || has(v.vin);
  /* No plate to fall back on, so the serial, a brand and model together, or
     a description somebody actually wrote. */
  return has(v.vin) || (has(v.make) && has(v.model)) || has(v.description);
}

/**
 * WHICH BOXES THE IDENTIFY STEP HAS TO PUT ON THE SCREEN.
 *
 * Anthony, 2026-09-18: "they still have vehicle or vin on their start job
 * tab."
 *
 * The technician's walk asked every trade for a number plate and a VIN,
 * which for the four trades with hasPlate false was not merely the wrong
 * words -- it was a step with no exit. isIdentified() above does not look
 * at license_plate for those trades, so a plate typed in was saved, the
 * gate still read false, and Save it appeared to do nothing.
 *
 * The fix is not to reword the boxes. It is to derive them from the same
 * rule that judges them, so a screen can never again offer a field that
 * cannot satisfy the gate it sits in front of. trade.test.ts fills in
 * exactly what this returns, for every trade, and asserts isIdentified()
 * then says yes.
 *
 * `description` is not offered anywhere: it is written by the intake
 * agent from what the customer said, not typed by somebody standing in
 * front of the thing.
 */
/**
 * "a part" BUT "an expense".
 *
 * Anthony, 2026-09-18: "do the vocabulary pass for technician and parts."
 *
 * Buttons that read "Add a part" and "Find a part" became "Add a expense"
 * the moment the noun came out of the vocabulary. Nobody writes that, and
 * on a screen a client can be shown it reads as a machine talking.
 *
 * Vowel-letter is the rule rather than vowel-sound, because every word
 * this is ever handed is a noun somebody typed into the vocabulary above
 * -- part, expense, lock, door, case. "an hour" and "a university" are
 * real exceptions and none of them are in this file; if one ever is, it
 * belongs in the vocabulary as a written-out phrase, not in a guesser.
 */
export function anA(noun: string): string {
  const n = String(noun ?? "").trim();
  if (!n) return "";
  /* AN INITIALISM IS SAID LETTER BY LETTER. "RV" is "ar-vee", so it takes
     "an" though it starts with a consonant, the same as "an HVAC unit" and
     "an SUV". Only all-capital words, so every lower case noun above keeps
     the vowel-letter rule it has always had. */
  if (/^[A-Z]{2,}$/.test(n.split(/\s/)[0]!)) {
    return `${"AEFHILMNORSX".includes(n[0]!) ? "an" : "a"} ${n}`;
  }
  return `${"aeiou".includes(n[0]!.toLowerCase()) ? "an" : "a"} ${n}`;
}

export function identifyAsks(trade: string | null | undefined): {
  plate: boolean; serial: boolean; makeModel: boolean;
} {
  const w = words(trade);
  return w.hasPlate
    ? { plate: true, serial: true, makeModel: false }
    : { plate: false, serial: true, makeModel: true };
}

const WORDS: Record<Trade, Vocabulary> = {
  auto: {
    hasPartsCatalog: true,
    thing: "vehicle",
    things: "vehicles",
    theirs: "your car",
    unidentified: "Unidentified vehicle",
    serial: "VIN",
    reading: "odometer",
    searchBy: "make, model, plate or VIN",
    hasPlate: true,
    hasEngine: true,
    trimLabel: "Trim",
    whyItMatters: "The price comes off the car, so year, make and model are worth "
      + "getting right before the quote goes.",
    example: "2014 Toyota Tundra",
    serialHint: "17 characters off the door jamb",
    makeExample: "Toyota",
    modelExample: "Camry",
    makeLabel: "Make",
    modelLabel: "Model",
    yearLabel: "Year",
    /* Never shown on a car before now, and this is not the change to start.
       The column is still written by the AI agent either way. */
    hasColour: false,
    colourLabel: "Color",
    complaintLabel: "Complaint",
    complaintHint: "What they reported",
    identifyTitle: "The car",
    identifySub: "A plate or a VIN. The job cannot close without one.",
    customerTitle: "Your Garage",
    customerOpening: "Opening your garage",
    customerReport: "Vehicle inspection report",
    /* Byte for byte what lead-textback.ts sent before these moved here.
       Supercanic and Wrench read these every day and nothing changed. */
    missedCallAsk: "What's the year, make and model, and what's it doing? "
      + "We'll get you a real price.",
    nudgeAsk: "do you need a mechanic?",
    textedInAsk: "What year, make and model?",
    textedInNext: "What's the address, and when are you free?",
    textedInPromise: " I'll get you a price.",
    portalLabel: "your garage page",
    nightAsk: "What's the vehicle, what's it doing, and where are you?",
    formAsk: "When are you free and what's the address?",
    identifyBlocked: "The car has no plate and no VIN. One of them has to be "
      + "on file before this closes.",
    /* A garage, a door company and a lock shop all say technician, and
       all three fit real parts. Unchanged on purpose. */
    techLabel: "technician",
    techLabelPlural: "technicians",
    partLabel: "part",
    partsLabel: "parts",
    partNumberLabel: "part number",
    quoteIsVisit: false,
    quoteLabel: "quote",
  },
  garage_door: {
    /* Real parts, real part numbers, and none of them in a
       vehicle catalog. Not the same question as partNumberLabel. */
    hasPartsCatalog: false,
    thing: "door",
    things: "doors",
    theirs: "your door",
    unidentified: "Unidentified door",
    /* Doors and openers both carry one, and a technician standing at the
       opener reads it off the same sticker either way. */
    serial: "serial number",
    /* A door's wear is in cycles, and nothing reports them unless the
       opener is a model that counts. Asking for a number nobody can read
       off the unit is how a required field becomes a guess, so the shop is
       not asked for one at all. */
    reading: null,
    searchBy: "make, model or serial number",
    hasPlate: false,
    hasEngine: false,
    /* A door's equivalent of a trim level is what it is made of -- steel,
       wood, aluminium, glass -- which changes the parts and the price the
       way an engine does on a car. */
    trimLabel: "Material",
    whyItMatters: "The price comes off the door, so make, model and material are "
      + "worth getting right before the quote goes.",
    example: "16x7 Clopay steel, LiftMaster opener",
    /* On a door it is on a sticker inside the track or on the opener
       housing, and which of the two depends on what is being fixed. */
    serialHint: "off the sticker in the track or on the opener",
    makeExample: "Clopay",
    modelExample: "Gallery",
    /* Clopay, Amarr, Wayne Dalton, Overhead Door. The trade says brand. */
    makeLabel: "Brand",
    modelLabel: "Model",
    /* A door is not a model year. The number that matters is when it went
       up, because it dates the springs and the opener with it. */
    yearLabel: "Year installed",
    /* The one field this trade needs that a car screen never showed. A
       replacement panel has to match what is already on the house, and
       "white" versus "almond" is the difference between a job that looks
       finished and one that has to be done again. */
    hasColour: true,
    colourLabel: "Color",
    complaintLabel: "Repair needed",
    complaintHint: "Will not close, bent panel, spring snapped",
    identifyTitle: "The door",
    identifySub: "A serial number, or the brand and model. The job cannot close without one.",
    customerTitle: "Your Door",
    customerOpening: "Opening your page",
    customerReport: "Door inspection report",
    /* No price by text any more. Anthony, 2026-09-29: the quote is "the
       customer saying yes to a free visit for an estimate", so the opener
       promises the visit rather than a number nobody is going to send. */
    missedCallAsk: "What's the door doing, and what brand is it? "
      + "The estimate is free.",
    nudgeAsk: "do you still need the door looked at?",
    textedInAsk: "What brand is the door, and what's it doing?",
    textedInNext: "What's the address, and when are you free?",
    textedInPromise: " The estimate is free.",
    portalLabel: "your door page",
    /* A spring goes at six in the morning and the car is shut in, so the
       after-hours call is real for this trade. */
    nightAsk: "What's the door doing, and where are you?",
    formAsk: "When are you free and what's the address?",
    identifyBlocked: "Nothing on the record says which door this was. Add the serial "
      + "number, or the brand and model, before this closes.",
    /* A garage, a door company and a lock shop all say technician, and
       all three fit real parts. Unchanged on purpose. */
    techLabel: "technician",
    techLabelPlural: "technicians",
    partLabel: "part",
    partsLabel: "parts",
    partNumberLabel: "part number",
    /* The first thing Swift sells is a visit, not a number. See quoteIsVisit
       above and src/domain/visitquote.ts. */
    quoteIsVisit: true,
    quoteLabel: "free estimate visit",
  },
  /**
   * ─── A LOCKSMITH WORKS ON THREE DIFFERENT SHAPES ───────────────────
   *
   * Anthony, 2026-09-17: a shop desk for abclockandsafe.com, Portland.
   * ABC runs four service lines -- residential, commercial, automotive
   * and safe cracking -- so unlike a repair shop or a door shop, the
   * thing a job hangs off is not one kind of object. It is a house with
   * four exterior doors, or a storefront on a master key, or a truck
   * needing a chipped key, or a 1920s floor safe nobody has the
   * combination to.
   *
   * They still ride in `vehicles`, for the reason 0113 gives: the columns
   * are year/make/model/serial/description and all four shapes have
   * those. What they do not share is a plate, which is why hasPlate is
   * false -- see below, it is the single most consequential line here.
   *
   * The umbrella word is "lock". It is what the trade is named for, it
   * is true of all four shapes (a safe is a lock, an ignition is a lock,
   * a house is a set of them), and it survives the sentences the server
   * builds out loud: "the address where the lock is", "everything done
   * to this lock". "Property" fails on a truck and "site" fails on a
   * safe, so neither can be the word in a sentence a customer reads.
   */
  locksmith: {
    thing: "lock",
    things: "locks",
    theirs: "your locks",
    unidentified: "Unidentified lock",
    /* A safe has one on the dial ring or the door edge, a car has a VIN,
       and a cylinder has nothing at all -- which is what description is
       for. One word covers the two that carry a number. */
    serial: "serial number",
    /* Nothing on a lock counts anything. */
    reading: null,
    searchBy: "brand, model, serial number or the address",
    /**
     * THE LINE THAT DECIDES WHETHER THIS SHOP CAN CLOSE A JOB.
     *
     * hasPlate does two jobs: it shows the plate field, and it makes
     * isIdentified() demand a plate or a VIN. Three of ABC's four service
     * lines are on buildings and safes, which have neither -- so leaving
     * this true would make every rekey, every lockout and every safe job
     * unclosable, the same shape of bug that held every Swift quote.
     *
     * False, so a job says which lock it was by its serial, by brand and
     * model, or by the line the technician wrote. Car key work loses the
     * plate PILL on the browse screen and nothing else: the plate still
     * goes in the description, where the search already looks.
     */
    hasPlate: false,
    hasEngine: false,
    /* The field a locksmith actually wants in this slot. KW1, SC1, SC4,
       and it is what decides whether the van leaves with the right blanks
       on it -- the whole point of keeping a record of the second visit. */
    trimLabel: "Keyway",
    whyItMatters: "The price comes off the hardware, so the brand, model and "
      + "keyway are worth getting right before the quote goes.",
    example: "Front door deadbolt, Kwikset, KW1",
    /* A safe wears it on the dial ring or the edge of the door; a lock
       body carries it stamped on the face. Neither is where somebody
       looks first, so the hint says both. */
    serialHint: "off the dial ring, or stamped on the lock body",
    makeExample: "Schlage",
    modelExample: "B60N",
    /* Schlage, Medeco, Kwikset, Sargent and Greenleaf. Brand, not make. */
    makeLabel: "Brand",
    modelLabel: "Model",
    /* The year the hardware went on, which dates the keyway and tells a
       technician whether parts are still made for it. */
    yearLabel: "Year installed",
    /* A replacement cylinder has to match the ones either side of it, and
       satin nickel next to oil rubbed bronze is a job done twice. */
    hasColour: true,
    colourLabel: "Finish",
    complaintLabel: "Work needed",
    complaintHint: "Locked out, rekey 4 doors, safe will not open",
    identifyTitle: "The lock",
    identifySub: "A serial number, a brand and model, or a line saying which "
      + "one it was. The job cannot close without one.",
    identifyBlocked: "Nothing on the record says which lock this was. Add the "
      + "serial number, the brand and model, or a line describing it, before "
      + "this closes.",
    /* Their customers are not opening a garage. */
    /* A garage, a door company and a lock shop all say technician, and
       all three fit real parts. Unchanged on purpose. */
    techLabel: "technician",
    techLabelPlural: "technicians",
    partLabel: "part",
    partsLabel: "parts",
    partNumberLabel: "part number",
    quoteIsVisit: false,
    quoteLabel: "quote",
    /* Cylinders and keyways have part numbers. A vehicle parts
       catalog still has nothing in it for a safe job. */
    hasPartsCatalog: false,
    customerTitle: "Your Locks",
    customerOpening: "Opening your page",
    customerReport: "Lock and key report",
    missedCallAsk: "What's going on, and where are you? "
      + "We'll get you a real price.",
    nudgeAsk: "do you still need a locksmith?",
    /* Never "what kind of lock" -- somebody standing outside their own
       front door at midnight does not know and should not be quizzed. */
    textedInAsk: "What's going on, and where are you?",
    textedInNext: "What's the address, and when can we come?",
    textedInPromise: " I'll get you a price.",
    portalLabel: "your page",
    nightAsk: "What's going on, and where are you?",
    formAsk: "When are you free and what's the address?",
  },
  /**
   * ─── AN INVESTIGATOR'S JOB HANGS OFF NO OBJECT AT ALL ──────────────
   *
   * Anthony, 2026-09-17: a desk for newyorkinvestigations.com. David
   * Schassler has run New York Intelligence Agency since 1989 and sells
   * labor and nothing else -- matrimonial surveillance, corporate work,
   * background checks, skip traces, TSCM sweeps.
   *
   * The first three trades all work on a THING: a car, a door, a lock.
   * This one does not. There is no object on a bench at the end of it,
   * and the four service lines do not even share a subject -- a
   * surveillance is about a person, a sweep is about a room, a skip
   * trace is about somebody nobody can find.
   *
   * The umbrella word is "case", and it is the only one that survives
   * every sentence the server builds out loud. "Subject" fails the
   * moment the customer reads it: `theirs` goes into a quote chase text
   * as "did you get the quote for YOUR SUBJECT", which is both cold and
   * wrong -- the client is not the subject, and in a matrimonial matter
   * saying so is close to an accusation. "Matter" fails as a heading a
   * stressed person scans. "Your case" is what they already call it on
   * the phone.
   *
   * ─── WHY IT STILL RIDES IN `vehicles` ──────────────────────────────
   *
   * The same reason 0113 gives for a door and 0118 for a safe: the
   * columns are year/make/model/serial/description and a case has all
   * five. What changes is which are shown and what they are called. The
   * stretch is wider here than for any previous trade and that is worth
   * saying plainly rather than discovering later -- `make` is the
   * MATTER, `model` is the WORK, `trim` is the VENUE.
   *
   * ─── hasPlate IS FALSE, AND HERE IT IS NOT EVEN CLOSE ──────────────
   *
   * A surveillance case often does record a subject's plate, which is
   * exactly the trap: true would make the plate the thing a case cannot
   * close without, and a background check, a skip trace and a bug sweep
   * have no plate anywhere in them. Every one of those would be
   * unclosable -- the Swift bug and the ABC bug for a third time.
   *
   * ─── THE ONE THING THIS TRADE CARRIES THAT NO OTHER DOES ───────────
   *
   * These records are more sensitive than anything else on this desk. A
   * matrimonial subject may share a phone with the client, and every
   * customer-facing string below was chosen on the assumption that
   * somebody who should not see it eventually will. `customerTitle` is
   * "Your Case" and not "Your Investigation"; the report is a "Case
   * report" and not a "Surveillance report". Nothing here names a
   * subject, a suspicion or a service. Before the customer portal is
   * turned on for this trade at all, somebody has to decide that
   * deliberately -- it is not a default this module can make safe.
   */
  investigations: {
    thing: "case",
    things: "cases",
    theirs: "your case",
    unidentified: "Unnumbered case",
    /* A firm opens a file and numbers it; it is on the engagement letter
       and it is what the client quotes back on the phone. */
    serial: "case number",
    /* Nothing about a case counts hours the way an odometer counts miles.
       The hours live on the quote, which is the whole product -- see
       src/domain/surveillance.ts. */
    reading: null,
    searchBy: "case number, the matter or the client",
    /**
     * FALSE, AND THE TRAP IS THAT A PLATE IS OFTEN PRESENT.
     *
     * A surveillance case really does record the subject's vehicle. True
     * would make that plate the thing every case must have before it can
     * close, and three of the four service lines never touch a car. The
     * plate keeps going in the description, where the search already
     * looks.
     */
    hasPlate: false,
    hasEngine: false,
    /**
     * Where this has to stand up. A conversation the client only wants
     * for themselves and evidence going into a custody hearing are not
     * the same work at the same hourly rate -- the second changes what
     * gets documented and how, and therefore the hours. It belongs next
     * to the identity fields because it is decided at intake, not at the
     * invoice.
     */
    trimLabel: "Venue",
    whyItMatters: "The price comes off the scope, so the matter, the work and where "
      + "it has to stand up are worth getting right before the quote goes.",
    example: "Matrimonial surveillance, Midtown, two evenings",
    serialHint: "the file number on the engagement letter",
    makeExample: "Matrimonial",
    modelExample: "Surveillance",
    /* Matrimonial, custody, corporate, fraud, missing person, TSCM. */
    makeLabel: "Matter",
    /* Surveillance, background check, skip trace, bug sweep, polygraph.
       The matter is why; the work is what is actually being sold. */
    modelLabel: "Work",
    yearLabel: "Year opened",
    hasColour: false,
    colourLabel: "Color",
    complaintLabel: "What they need to know",
    complaintHint: "Is my husband seeing someone, is my office bugged, find this person",
    identifyTitle: "The case",
    identifySub: "A case number, or the matter and the work. The case cannot close without one.",
    identifyBlocked: "Nothing on the record says which case this was. Add the case number, "
      + "or the matter and the work, before this closes.",
    /**
     * THE TWO WORDS A FIRM THAT SELLS HOURS DOES NOT USE.
     *
     * Nobody at NYIA is a technician and nothing they bill is a part.
     * What they do bill beside the hours is real -- the vehicle fee per
     * day, database searches, filing fees -- so the line is not removed,
     * it is called what it is. "Parts" on an invoice a matrimonial
     * client reads is the tell that the software was built for a garage.
     *
     * The Role value is still `technician` and the line kind is still
     * `part`. Only the labels move.
     */
    techLabel: "investigator",
    techLabelPlural: "investigators",
    partLabel: "expense",
    partsLabel: "expenses",
    /* No catalog number on a filing fee. Null hides the box. */
    partNumberLabel: null,
    quoteIsVisit: false,
    quoteLabel: "quote",
    /* Nothing they buy is in any catalog. */
    hasPartsCatalog: false,
    /* See the field. Neelum's list is the whole product for now. */
    caseFile: true,
    /* Deliberately the blandest words in this file. See the note above:
       assume the wrong person is holding the phone. */
    customerTitle: "Your Case",
    customerOpening: "Opening your page",
    customerReport: "Case report",
    /**
     * NO PRICE IS PROMISED, AND NOTHING IS NAMED.
     *
     * The other trades end "we'll get you a real price", which they can
     * keep -- a brake job prices off a text. Surveillance does not: it is
     * hours against a retainer and it has to be scoped by a person, and
     * the drafter refuses rather than guess. Promising a price here is
     * promising something nothing downstream can deliver.
     *
     * And it asks a WIDE question on purpose. "What's the matter about"
     * invites somebody to type the worst thing in their life into a text
     * to a number they have not met. The desk needs enough to route it
     * and no more; David asks the rest on the phone.
     *
     * Read every one of these as a person whose husband may be holding
     * the phone. Nothing here says investigation, surveillance, or why
     * they might have called.
     */
    missedCallAsk: "Let us know what you need and we'll come straight back to you.",
    nudgeAsk: "did you still want to talk?",
    textedInAsk: "What can we help with?",
    textedInNext: "When is a good time to call you?",
    textedInPromise: "",
    portalLabel: "your case page",
    /* There is no after-hours rate here. They are 24/7 at one price, and
       the night copy would invent one off a column nobody set. */
    nightAsk: null,
    formAsk: "When's a good time to talk?",
  },
  /**
   * ─── A SHOP THAT SELLS EXACTLY ONE THING ───────────────────────────
   *
   * Anthony, 2026-09-17: a desk for JC's Mobile Headlight Restoration in
   * Moreno Valley. Javier restores headlights. That is the whole menu.
   *
   * ─── WHY THIS IS NOT `auto` ────────────────────────────────────────
   *
   * The object a job hangs off IS a car, so at first glance auto already
   * fits. What does not fit is everything auto asks about the car. An
   * auto desk asks for an odometer reading, offers an engine field, calls
   * the `trim` column Trim, and hands the customer a page titled "Your
   * Garage" carrying a "Vehicle inspection report".
   *
   * None of that belongs on a job that takes an hour on a driveway and
   * never touches anything under the hood. A required field nobody can
   * answer is how a shop learns to leave fields blank, and a customer
   * link that says "Your Garage" about two headlights reads as somebody
   * else's software.
   *
   * ─── hasPlate IS FALSE, AND IT IS THE LINE THAT MATTERS ────────────
   *
   * Same rule as the locksmith, reached from the opposite direction.
   * hasPlate decides what satisfies "say which one you worked on" before
   * a job can close, and for auto that is a plate or a VIN and nothing
   * else -- not even a make and model.
   *
   * Javier works on driveways for fifty dollars and is gone in an hour.
   * He will have "2014 Nissan Sentra", which is what his own customers
   * write in their reviews. He will very often not have walked round to
   * read the plate, and he will never have the VIN off the door jamb.
   *
   * Left true, every single job on this desk would be unclosable -- the
   * exact bug that held every Swift quote and would have held every ABC
   * rekey. False means the year, make and model identifies the car, which
   * for a headlight job it genuinely does. The plate field goes away with
   * it and the plate can live in the description, where the search
   * already looks.
   *
   * ─── WHAT THE FIELDS BECOME ────────────────────────────────────────
   *
   * `trim` is the LENS TYPE. Halogen, projector and LED units are not the
   * same job -- a projector bowl and a sealed LED housing sand
   * differently and price differently -- and it is the one thing worth
   * knowing before quoting.
   *
   * `colour` is ON, which auto has never had. A mobile tech is looking
   * for one car in an apartment lot from a text message. "Silver Accord"
   * finds it. "Accord" does not.
   *
   * `reading` is null, because nobody has ever needed an odometer to sand
   * a lens, and hasEngine is false for the same reason.
   */
  headlight: {
    thing: "vehicle",
    things: "vehicles",
    theirs: "your headlights",
    unidentified: "Unidentified vehicle",
    serial: "VIN",
    /* Nothing about a lens wears out by the mile. It wears out by the
       hour parked in the sun, and no gauge counts that. */
    reading: null,
    searchBy: "make, model or color",
    /* THE LINE ABOVE. False, or no job on this desk ever closes. */
    hasPlate: false,
    hasEngine: false,
    /* Halogen, projector, LED. It changes the work and it changes the
       price, which is exactly what this slot is for. */
    trimLabel: "Lens type",
    whyItMatters: "The price comes off the lens, so the year, make and model are "
      + "worth getting right before the quote goes.",
    example: "2014 Nissan Sentra, both lenses yellowed",
    serialHint: "17 characters off the door jamb, only if you need it",
    makeExample: "Honda",
    modelExample: "Accord",
    makeLabel: "Make",
    /* A car does have a model, so this is the ordinary word. It became a
       field with the investigator trade, whose record is a matter rather
       than an object. */
    modelLabel: "Model",
    yearLabel: "Year",
    /* On for this trade and off for auto, because this technician is
       finding one car in a lot from a text rather than greeting it into a
       bay. */
    hasColour: true,
    colourLabel: "Color",
    complaintLabel: "Headlight condition",
    complaintHint: "Yellow on top, hazy, cloudy inside",
    identifyTitle: "The car",
    identifySub: "The year, make and model. The job cannot close without it.",
    identifyBlocked: "Nothing on the record says which car this was. Add the "
      + "year, make and model, or a line describing it, before this closes.",
    /* A garage, a door company and a lock shop all say technician, and
       all three fit real parts. Unchanged on purpose. */
    techLabel: "technician",
    techLabelPlural: "technicians",
    partLabel: "part",
    partsLabel: "parts",
    partNumberLabel: "part number",
    quoteIsVisit: false,
    quoteLabel: "quote",
    /* The record is a car, so the catalog applies: lenses and
       assemblies are in it under the same year, make and model. */
    hasPartsCatalog: true,
    /* His customers are not opening a garage and they are not reading an
       inspection report. They are looking at two headlights. */
    customerTitle: "Your Headlights",
    customerOpening: "Opening your page",
    customerReport: "Headlight restoration report",
    missedCallAsk: "What's the year, make and model? "
      + "We'll get you a real price.",
    nudgeAsk: "do you still want the headlights done?",
    textedInAsk: "What year, make and model?",
    textedInNext: "What's the address, and when are you free?",
    textedInPromise: " I'll get you a price.",
    portalLabel: "your page",
    /* Nobody restores a headlight at midnight. */
    nightAsk: null,
    formAsk: "When are you free and what's the address?",
  },

  /**
   * ─── MARKETING. THE FIRST TRADE WHOSE CUSTOMER IS A BUSINESS ───────
   *
   * Clickflame, 2026-09-22. Every trade above sells a job on a thing --
   * a car, a door, a lock, a case. This one sells months of work on
   * somebody else's BUSINESS, and the person texting is the owner of it.
   *
   * ─── WHY IT IS NOT ENOUGH TO LEAVE THE TRADE UNSET ─────────────────
   *
   * words() falls back to auto, so a Clickflame account with no trade
   * would have texted a prospective SEO client "What's the year, make
   * and model, and what's it doing?". That is [[a-bot-denies-what-its-
   * prompt-omits]] pointing the other way: not a bot refusing something
   * it does, but a bot confidently asking about a car nobody mentioned.
   *
   * ─── NO PRICE IS PROMISED ──────────────────────────────────────────
   *
   * textedInPromise is empty, like investigations. A brake job prices
   * off a text; a retainer does not, and the offer is a free build plus
   * $800 a month which has to be said by a person who has looked at the
   * market first. Promising a price here promises something nothing
   * downstream can deliver.
   *
   * ─── THE ASK IS THE BUSINESS, NOT THE SERVICE ──────────────────────
   *
   * "What's the business and where are you" beats "do you want SEO or
   * paid ads". Somebody who knew which one they needed would not be
   * ringing an agency, and the name plus the town is the whole of what
   * is needed to pull their rankings up before replying -- which is the
   * thing that makes the reply worth reading.
   */
  marketing: {
    thing: "business",
    things: "businesses",
    theirs: "your business",
    unidentified: "Unnamed business",
    /* The domain is the identifier. It is unique, the client knows it by
       heart, and it is the one string every tool here keys off. */
    serial: "website",
    /* Nothing counts up the way an odometer does. What a campaign is
       worth is on the report, not on the record. */
    reading: null,
    searchBy: "business name, website or city",
    hasPlate: false,
    hasEngine: false,
    /* Where they sell. Two plumbers on the same street are one market;
       a plumber in Hemet and a plumber in Tampa are two, and the price
       and the plan differ because the competition does. */
    trimLabel: "Market",
    whyItMatters: "The plan comes off the market, so the trade, the town and the "
      + "website are worth getting right before anybody quotes.",
    example: "Plumber, Riverside CA, riversideplumbing.com",
    serialHint: "the domain they already own, even if the site is bad",
    makeExample: "Plumbing",
    modelExample: "Local SEO",
    /* What they do for a living. */
    makeLabel: "Trade",
    /* What we would sell them. The trade is who they are; this is the
       work. */
    modelLabel: "Service",
    yearLabel: "In business since",
    hasColour: false,
    colourLabel: "Color",
    complaintLabel: "What is not working",
    complaintHint: "The phone stopped ringing in March, we are on page 4, the site is not mobile",
    identifyTitle: "The business",
    identifySub: "A website, or the trade and the town. Nothing can be quoted without one.",
    identifyBlocked: "Nothing on the record says which business this was. Add the website, "
      + "or the trade and the town, before this closes.",
    /* Nobody here is a technician and nothing billed is a part. What sits
       beside the hours is real -- ad spend, a directory listing, a
       stock photo license -- so the line stays and is called what it is. */
    techLabel: "specialist",
    techLabelPlural: "specialists",
    partLabel: "cost",
    partsLabel: "costs",
    partNumberLabel: null,
    quoteIsVisit: false,
    quoteLabel: "quote",
    hasPartsCatalog: false,
    customerTitle: "Your Account",
    customerOpening: "Opening your page",
    customerReport: "Performance report",
    missedCallAsk: "What's the business and where are you? "
      + "I'll look before I call back.",
    nudgeAsk: "did you still want us to look at your marketing?",
    textedInAsk: "What's the business and where are you?",
    textedInNext: "What's not working right now?",
    textedInPromise: "",
    portalLabel: "your account page",
    /* An agency has no after-hours rate and never has. Null rather than
       an invented one -- see the headlight note, where reading this off
       a different column quoted a night rate that does not exist. */
    nightAsk: null,
    formAsk: "What's the website, and when's a good time to talk?",
  },

  /**
   * ─── RV ────────────────────────────────────────────────────────────────
   *
   * Anthony, 2026-09-26: "RV Desk", this desk for RV rental businesses --
   * priced "$10 a rental", "custom to their business and fleet" -- with
   * rvtravelagency.com as the directory that sends them work. The records
   * are the fleet's own units, and a job on one is its upkeep.
   *
   * THE RECORD IS STILL A VEHICLE. It has a plate and a VIN, it is found
   * in the federal recall database, and a job on it closes on the same
   * rule as a car's: a plate or a VIN. So hasPlate and hasPartsCatalog
   * are auto's answers, and the chassis side of the manuals shelf -- a
   * Ford F-53 under a Class A, a Sprinter under a Class B -- is real.
   *
   * hasEngine IS FALSE, ON PURPOSE. Most of what an RV tech is called for
   * is the coach, not the chassis: slides, roofs, awnings, the fridge, the
   * water heater, the generator, LP. Half the fleet is towables with no
   * engine at all. hasEngine is the switch the process pages read to say
   * "the car" and "an unknown noise", and the one the quote check reads to
   * demand a parsed year, make and model -- which a fifth wheel's "2021
   * Grand Design Solitude" does not always parse into. With it off, those
   * pages say "the RV" and the quote needs the RV named, not parsed.
   *
   * THE CLASS LIVES IN `trim`. See src/domain/rvclass.ts, which reads it.
   *
   * The stranger copy asks about the trip, not the vehicle: see the note
   * on missedCallAsk below.
   */
  rv: {
    hasPartsCatalog: true,
    thing: "RV",
    things: "RVs",
    theirs: "your RV",
    unidentified: "Unidentified RV",
    serial: "VIN",
    /* A motorhome has one and a trailer does not. Kept, because it is the
       warranty's mileage on a Class A, and a towable just leaves it empty. */
    reading: "odometer",
    searchBy: "make, model, class, plate or VIN",
    hasPlate: true,
    hasEngine: false,
    trimLabel: "Class",
    trimExample: "Class A, Class C, fifth wheel, travel trailer",
    whyItMatters: "The price comes off the RV, so the class, year, make and model are "
      + "worth getting right before the quote goes.",
    example: "2019 Winnebago View 24D",
    serialHint: "17 characters, on the chassis or the frame tag",
    makeExample: "Winnebago",
    modelExample: "View",
    makeLabel: "Make",
    modelLabel: "Model",
    yearLabel: "Year",
    hasColour: false,
    colourLabel: "Color",
    complaintLabel: "Complaint",
    complaintHint: "What they reported. Slide stuck, fridge warm, roof leak",
    identifyTitle: "The RV",
    identifySub: "A plate or a VIN. The job cannot close without one.",
    identifyBlocked: "The RV has no plate and no VIN. One of them has to be "
      + "on file before this closes.",
    customerTitle: "Your RV",
    customerOpening: "Opening your RV page",
    customerReport: "RV inspection report",
    techLabel: "technician",
    techLabelPlural: "technicians",
    partLabel: "part",
    partsLabel: "parts",
    partNumberLabel: "part number",
    quoteIsVisit: false,
    quoteLabel: "quote",
    /* THE CALLER WANTS TO RENT ONE. Anthony, 2026-09-26: "$10 a rental",
       "custom to their business and fleet". An RV Desk shop is a rental
       business, so a stranger ringing it is planning a trip, and the two
       facts that decide what can be offered are the dates and how many
       are going. Asking what the RV is doing would be a repair shop's
       question put to a camper. */
    missedCallAsk: "What dates, and how many are going? We'll send you what's open.",
    nudgeAsk: "are you still looking at an RV for your trip?",
    textedInAsk: "What dates, and how many are going?",
    textedInNext: "Where are you headed? Want it delivered?",
    textedInPromise: " I'll send what's open.",
    portalLabel: "your trip page",
    /* No night rate until a shop sets one. The night opener prices off
       labour_rate ?? 150 and calls it double, and an RV shop that never
       agreed to that must not be texting it to strangers at eleven. */
    nightAsk: null,
    formAsk: "What dates, and how many are going?",
    /* The opener above already asks for dates and head count. The rest of
       the conversation has to carry on in the same key. */
    intakeKind: "rental",
  },
  /**
   * ─── EVERY OTHER TRADE THAT DRIVES TO A HOUSE OR A BUSINESS ──────
   *
   * Anthony, 2026-10-12: "Shop Desk signup should be at yourshopdesk.com
   * now and work. Should be for every industry, not just mechanics."
   * Roofers, plumbers, HVAC, electricians, appliance repair, cleaning,
   * landscaping and whatever "other" turns out to be.
   *
   * Without a row of its own each of them would have fallen back to auto
   * (see words() below) and been asked for a VIN on the first job. They
   * share one row because what they share is the thing that decides the
   * screens: the work happens at a PROPERTY, there is no plate, no
   * odometer, no vehicle parts catalog, and an estimate is priced by
   * somebody who has seen it. What they do not share (a roof, a water
   * heater, a lawn) goes in the job's own words, the way the locksmith
   * row carries four shapes under one noun.
   *
   * "Property" is the umbrella because it survives the sentences the
   * server builds: "the quote for your property", "everything done to
   * this property", and it is true of a house, a shop and a yard alike.
   * An appliance or a unit is identified by its serial or brand and
   * model, which is what the identify step offers for every trade with
   * no plate.
   *
   * The check constraint has to allow 'service' before an account can
   * hold it (migration 0211). Until then the signup route saves the shop
   * on the column default and says so; see server/routes/signup.ts.
   */
  service: {
    hasPartsCatalog: false,
    thing: "property",
    things: "properties",
    theirs: "your property",
    unidentified: "Unidentified property",
    serial: "unit or serial number",
    /* Nothing at a house counts up the way an odometer does. */
    reading: null,
    searchBy: "address, brand, model or serial number",
    hasPlate: false,
    hasEngine: false,
    trimLabel: "Type",
    trimExample: "Single story, tile roof, split system, 200 amp panel",
    whyItMatters: "The price comes off what is there, so the type, brand and model "
      + "are worth getting right before the quote goes.",
    example: "Two story house, 2016 Carrier split system",
    serialHint: "on the unit's rating plate, if it has one",
    makeExample: "Carrier",
    modelExample: "Infinity 24",
    makeLabel: "Brand",
    modelLabel: "Model or type",
    yearLabel: "Year built or installed",
    hasColour: false,
    colourLabel: "Color",
    complaintLabel: "Work needed",
    complaintHint: "Roof leak, no hot water, breaker keeps tripping",
    identifyTitle: "The property",
    identifySub: "A serial number, or the brand and model or type. The job cannot close without one.",
    identifyBlocked: "Nothing on the record says what this job was on. Add a serial "
      + "number, or the brand and model or type, before this closes.",
    customerTitle: "Your Property",
    customerOpening: "Opening your page",
    customerReport: "Inspection report",
    techLabel: "technician",
    techLabelPlural: "technicians",
    partLabel: "part",
    partsLabel: "parts",
    partNumberLabel: "part number",
    /* No night opener. It prices off labour_rate and calls it double, and
       a roofer or a cleaner never agreed to that. */
    missedCallAsk: "What do you need done, and where is it?",
    nudgeAsk: "do you still need a hand with this?",
    textedInAsk: "What do you need done, and where is it?",
    textedInNext: "What's the address, and when are you free?",
    /* A roof or a remodel is priced by somebody who has seen it, so the
       opener promises nothing it cannot keep. */
    textedInPromise: "",
    portalLabel: "your job page",
    nightAsk: null,
    formAsk: "When are you free and what's the address?",
    quoteIsVisit: false,
    quoteLabel: "quote",
  },

  /**
   * A REMODELER. Anthony, 2026-10-23: SRC Home Repair & Remodel (Alvin
   * Santos, River Grove IL), "trade is remodeling" and "it better not say
   * anything about cars". 'service' was close and still wrong: its
   * examples are a Carrier split system and a tripped breaker, and it asks
   * for a serial number before a job can close. A kitchen has no serial.
   *
   * Identified the way every trade with no plate is (isIdentified): a
   * permit number in the serial box, or the room and the scope together,
   * "Kitchen" and "Full remodel". The people are the crew and what gets
   * ordered is materials, because that is what a remodeler calls them.
   */
  remodeling: {
    hasPartsCatalog: false,
    thing: "project",
    things: "projects",
    theirs: "your project",
    unidentified: "Unnamed project",
    serial: "permit number",
    /* A room has nothing that counts up the way an odometer does. */
    reading: null,
    searchBy: "address, room, scope or permit number",
    hasPlate: false,
    hasEngine: false,
    trimLabel: "Size",
    trimExample: "12 x 14 kitchen, 2 bathrooms, 800 sq ft basement",
    whyItMatters: "The price comes off the room, the size and the finishes, so "
      + "measure it and write down what is there before the estimate goes.",
    example: "Kitchen, full remodel, about 150 sq ft",
    serialHint: "on the permit card, once the city issues one",
    makeExample: "Kitchen",
    modelExample: "Full remodel",
    makeLabel: "Room",
    modelLabel: "Scope",
    yearLabel: "Year the home was built",
    hasColour: false,
    colourLabel: "Color",
    complaintLabel: "What they want done",
    complaintHint: "New kitchen cabinets and counters, walk-in shower, finish the basement",
    identifyTitle: "The project",
    identifySub: "A permit number, or the room and the scope. The job cannot close without one.",
    identifyBlocked: "Nothing on the record says what this project was. Add a permit "
      + "number, or the room and the scope, before this closes.",
    customerTitle: "Your Project",
    customerOpening: "Opening your page",
    customerReport: "Walkthrough notes",
    techLabel: "crew member",
    techLabelPlural: "crew",
    partLabel: "material",
    partsLabel: "materials",
    partNumberLabel: "item number",
    missedCallAsk: "What would you like done, and what's the address?",
    nudgeAsk: "are you still planning your project?",
    textedInAsk: "What would you like done, and what's the address?",
    textedInNext: "What's the address, and when can we come take a look?",
    /* A remodel is priced by somebody who has walked it, so the opener
       promises a visit, never a number. */
    textedInPromise: "",
    portalLabel: "your project page",
    nightAsk: null,
    formAsk: "What's the address, and when can we come take a look?",
    quoteIsVisit: false,
    quoteLabel: "estimate",
  },
  /**
   * WATER DAMAGE, MOLD, FIRE AND SMOKE, SEWAGE AND STORM. Anthony,
   * 2026-10-28, for Peter Banyai's RemediationX and Nevada Water Damage
   * (Las Vegas): "build for water damage and what they do", "trade of
   * water damage". The record is the loss, not a thing somebody owns.
   *
   * The boxes the desk already has carry the identity: the loss type sits
   * in `make` (Water, Mold, Fire and smoke, Sewage, Storm), the IICRC
   * category and class in `model`, the rooms and square feet in `trim`,
   * and the insurance claim number in the serial box, so a job with a
   * claim number on it, or a loss type and its category, can close. The
   * rest of the claim and the drying log live on jobs.loss (0235), drawn
   * by the loss file card.
   */
  restoration: {
    hasPartsCatalog: false,
    lossFile: true,
    thing: "loss",
    things: "losses",
    theirs: "your home",
    unidentified: "Unnamed loss",
    serial: "claim number",
    /* Moisture is a log of many readings, not one number on the record,
       so it lives in the loss file and this box is hidden. */
    reading: null,
    searchBy: "address, loss type or claim number",
    hasPlate: false,
    hasEngine: false,
    trimLabel: "Affected area",
    trimExample: "Kitchen, hall and 2 bedrooms, about 600 sq ft",
    whyItMatters: "The adjuster pays off the loss type, the category and class and "
      + "the area, so write them down before anything gets torn out.",
    example: "Water, Cat 1 Class 2, kitchen and hall",
    serialHint: "on the carrier's email or the first notice of loss",
    makeExample: "Water",
    modelExample: "Cat 2, Class 3",
    makeLabel: "Loss type",
    modelLabel: "Category and class",
    yearLabel: "Year the home was built",
    hasColour: false,
    colourLabel: "Color",
    complaintLabel: "What happened",
    complaintHint: "Water heater burst in the garage, ceiling leak after the storm, mold behind the sink",
    identifyTitle: "The loss",
    identifySub: "A claim number, or the loss type and its category and class. The job cannot close without one.",
    identifyBlocked: "Nothing on the record says what this loss was. Add the claim "
      + "number, or the loss type and its category and class, before this closes.",
    customerTitle: "Your Home",
    customerOpening: "Opening your page",
    customerReport: "Drying report",
    techLabel: "tech",
    techLabelPlural: "techs",
    partLabel: "item",
    partsLabel: "equipment and materials",
    partNumberLabel: null,
    missedCallAsk: "Is water still coming in, and what's the address? We can be out today.",
    nudgeAsk: "do you still need help with the water damage?",
    textedInAsk: "Is water still coming in, and what's the address?",
    textedInNext: "What's the address, and is the water shut off?",
    /* Nobody prices a loss they have not seen, and most of it is billed
       to the carrier anyway, so the opener promises a visit, not a number. */
    textedInPromise: "",
    portalLabel: "your job page",
    nightAsk: null,
    formAsk: "Is water still coming in, and what's the address?",
    quoteIsVisit: false,
    quoteLabel: "estimate",
  },
};

/**
 * The words for a trade. An unknown or missing trade falls back to auto,
 * which is what every account was before 0107 -- a shop whose column is
 * somehow unset gets the old screens, not a screen full of "undefined".
 */
export function words(trade: string | null | undefined): Vocabulary {
  return WORDS[(trade ?? "auto") as Trade] ?? WORDS.auto;
}

/**
 * Which conversation the intake agent holds for this trade. Repair unless
 * the vocabulary says otherwise, so an unknown trade, a missing one and
 * every trade written before 2026-09-28 all answer exactly as before.
 */
export function intakeKind(trade: string | null | undefined): "repair" | "rental" {
  return words(trade).intakeKind === "rental" ? "rental" : "repair";
}

/** Sentence case, for a heading or a field label. */
export function cap(s: string): string {
  return s ? s[0]!.toUpperCase() + s.slice(1) : s;
}

/** Every trade this module has words for. The test walks it. */
export const TRADES = Object.keys(WORDS) as Trade[];
